/**
 * Presets modal - list, apply, create from drafts
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Filter, Layers, Plus, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';
import {
  useBulkPresetFolders,
  useCreatePresetFolder,
  useDeletePresetFolder,
  usePresetFolders,
  usePreviewPresetMatchingCount,
  useUpdatePresetFolder,
} from '@/hooks';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { getDefaultCampaign } from '@/features/smswiser-campaigns/utils/defaultCampaignStorage';

import { ApplyPresetModal } from './ApplyPresetModal';
import { CreatePresetModal } from './CreatePresetModal';
import { CreateRuleBasedPresetModal } from './CreateRuleBasedPresetModal';
import { EditPresetModal } from './edit';
import { EditRuleBasedPresetModal } from './EditRuleBasedPresetModal';
import { usePresetFolderBulk, usePresetsModal } from './hooks';
import { SortablePresetList } from './list';
import { sumPresetTemplateDrops } from './list/shared';
import { PresetsModalBulkBar } from './shell';
import { PresetFolderBulkBar, PresetFolderChips, PresetFoldersManageDialog } from './folders';
import { anyPresetNeedsBulkRouting, ruleBasedPresetNeedsApplyRouting } from './shared/preset-bulk-apply-routing.utils';
import type { Preset } from './shared';
import type { PresetsModalCatalog } from './shared/types';
import { backdropClick } from '@/components/ui';

export type { PresetsModalCatalog };
export type { EditPresetModalProps } from './edit';

export interface PresetsModalProps {
  countryKey: string;
  workDate: string;
  catalog: PresetsModalCatalog;
  campaignWiserId: number;
  selectedDrafts?: PreparedDrop[];
  onClose: () => void;
  onApplied?: () => void;
}

export const PresetsModal: FC<PresetsModalProps> = ({
  countryKey,
  workDate,
  catalog,
  campaignWiserId,
  selectedDrafts = [],
  onClose,
  onApplied,
}) => {
  const state = usePresetsModal(
    countryKey,
    workDate,
    campaignWiserId,
    onApplied,
    onClose,
    selectedDrafts.length,
    catalog.campaigns,
  );
  const folderBulk = usePresetFolderBulk(state.presets);
  const presetFoldersQuery = usePresetFolders(countryKey);
  const updatePresetFolder = useUpdatePresetFolder(countryKey);
  const deletePresetFolder = useDeletePresetFolder(countryKey);
  const createPresetFolder = useCreatePresetFolder(countryKey);
  const bulkPresetFolders = useBulkPresetFolders(countryKey);
  const [bulkWorkDate, setBulkWorkDate] = useState(workDate);
  const [bulkCampaignId, setBulkCampaignId] = useState(0);
  const [bulkRouteId, setBulkRouteId] = useState(0);
  const [bulkShortLinkTld, setBulkShortLinkTld] = useState('');
  const [manageFoldersOpen, setManageFoldersOpen] = useState(false);
  const folderMutationBusy =
    updatePresetFolder.isPending ||
    deletePresetFolder.isPending ||
    createPresetFolder.isPending ||
    bulkPresetFolders.isPending;
  const { success: toastFolderOk, error: toastFolderErr } = useToast();

  useEffect(() => {
    setBulkWorkDate(workDate);
  }, [workDate]);

  useEffect(() => {
    if (folderBulk.folderFilterId == null) return;
    const d = getDefaultCampaign(countryKey);
    const valid = d != null && catalog.campaigns.some((c) => c.id === d);
    setBulkCampaignId(valid && d != null ? d : campaignWiserId > 0 ? campaignWiserId : 0);
  }, [folderBulk.folderFilterId, countryKey, catalog.campaigns, campaignWiserId]);

  useEffect(() => {
    if (folderBulk.folderFilterId != null) return;
    setBulkCampaignId((prev) => {
      if (prev >= 1) return prev;
      const d = getDefaultCampaign(countryKey);
      if (d != null && catalog.campaigns.some((c) => c.id === d)) return d;
      if (campaignWiserId > 0 && catalog.campaigns.some((c) => c.id === campaignWiserId)) return campaignWiserId;
      return prev;
    });
  }, [folderBulk.folderFilterId, countryKey, catalog.campaigns, campaignWiserId]);

  const bulkSelectedPresets = useMemo(
    () => folderBulk.filtered.filter((p) => folderBulk.bulkSelected.has(p.id)),
    [folderBulk.filtered, folderBulk.bulkSelected],
  );

  const bulkSelectedDropCount = useMemo(
    () => sumPresetTemplateDrops(bulkSelectedPresets),
    [bulkSelectedPresets],
  );

  const anyCatalogPresetNeedsRouting = useMemo(
    () => state.presets.some(ruleBasedPresetNeedsApplyRouting),
    [state.presets],
  );

  const bulkSelectionNeedsRouting = useMemo(
    () => anyPresetNeedsBulkRouting(bulkSelectedPresets),
    [bulkSelectedPresets],
  );

  useEffect(() => {
    if (!anyCatalogPresetNeedsRouting) return;
    const g = catalog.gateways;
    const t = catalog.tlds;
    if (g.length === 0 || t.length === 0) return;
    setBulkRouteId((prev) => (prev > 0 && g.some((x) => x.id === prev) ? prev : g[0]!.id));
    setBulkShortLinkTld((prev) => (prev && t.some((x) => x.value === prev) ? prev : t[0]!.value));
  }, [anyCatalogPresetNeedsRouting, catalog.gateways, catalog.tlds]);

  const bulkRoutingFilled =
    !anyCatalogPresetNeedsRouting ||
    (bulkRouteId >= 1 &&
      bulkShortLinkTld.trim().length > 0 &&
      catalog.gateways.length > 0 &&
      catalog.tlds.length > 0);

  // Preview must use the campaign chosen in Apply Preset, not the page toolbar
  // campaign — otherwise dropCount/canApply stay wrong and "Create drops" stays disabled.
  const previewCountQuery = usePreviewPresetMatchingCount(
    countryKey,
    state.applyingPreset?.presetType === 'rule-based' ? state.applyingPreset?.id ?? null : null,
    state.applyCampaignWiserId > 0 ? state.applyCampaignWiserId : null,
  );
  const dropCount =
    state.applyingPreset?.presetType === 'rule-based'
      ? previewCountQuery.isLoading
        ? undefined
        : (previewCountQuery.data?.count ?? 0)
      : state.applyingPreset?.items?.length ?? 1;
  const dropLabels =
    state.applyingPreset?.presetType === 'rule-based'
      ? previewCountQuery.data?.listLabels?.map((l) => ({ id: String(l.wiserId), label: l.name })) ?? undefined
      : state.applyingPreset?.items?.map((item, i) => ({
          id: String(item.leadsListWiserId),
          label:
            catalog.leadsLists.find((l) => l.id === item.leadsListWiserId)?.name ?? `Drop ${i + 1}`,
        }));

  const [isApplyAiGenerating, setIsApplyAiGenerating] = useState(false);

  const handleApplyOverlayClick = () => {
    if (!isApplyAiGenerating) state.setApplyingPreset(null);
  };

  const bulkRoutingPayload = (): { routeId: number; shortLinkTldValue: string } | undefined =>
    bulkRouteId >= 1 && bulkShortLinkTld.trim()
      ? { routeId: bulkRouteId, shortLinkTldValue: bulkShortLinkTld.trim() }
      : undefined;

  const applyGroup = (ids: string[], match: boolean) => {
    const map = new Map(state.presets.map((p) => [p.id, p]));
    const list = ids.map((id) => map.get(id)).filter((p): p is Preset => p != null);
    void state.handleBulkApplyPresets(list, bulkWorkDate, bulkCampaignId, match, bulkRoutingPayload());
  };

  const applySelectedBulk = () => {
    void state.handleBulkApplyPresets(
      bulkSelectedPresets,
      bulkWorkDate,
      bulkCampaignId,
      folderBulk.matchListOrder,
      bulkRoutingPayload(),
    );
  };

  const handleFolderRename = async (id: string, label: string) => {
    try {
      await updatePresetFolder.mutateAsync({ id, label });
      toastFolderOk('Folder updated', `"${label}" saved.`);
    } catch (err) {
      toastFolderErr('Update failed', getApiErrorMessage(err, 'Could not update folder'));
    }
  };

  const handleFolderDelete = async (id: string) => {
    try {
      await deletePresetFolder.mutateAsync(id);
      if (folderBulk.folderFilterId === id) folderBulk.setFolderFilterId(null);
      toastFolderOk('Folder deleted', 'Removed from catalog and presets.');
    } catch (err) {
      toastFolderErr('Delete failed', getApiErrorMessage(err, 'Could not delete folder'));
    }
  };

  const handleCreateFolder = async (label: string) => {
    try {
      await createPresetFolder.mutateAsync(label.trim());
      toastFolderOk('Folder created', `"${label.trim()}" added.`);
    } catch (err) {
      toastFolderErr('Create failed', getApiErrorMessage(err, 'Could not create folder'));
    }
  };

  const selectedPresetIdList = useMemo(() => [...folderBulk.bulkSelected], [folderBulk.bulkSelected]);

  const runBulkFolder = (mode: 'add' | 'replace', folderId: string) => {
    const presetIds = selectedPresetIdList;
    if (presetIds.length === 0 || !folderId) return;
    bulkPresetFolders.mutate(
      { presetIds, mode, folderIds: [folderId] },
      {
        onSuccess: () =>
          toastFolderOk(
            'Folders updated',
            mode === 'add'
              ? `Added folder to ${presetIds.length} preset(s).`
              : `Set folder on ${presetIds.length} preset(s).`,
          ),
        onError: (err) =>
          toastFolderErr('Bulk update failed', getApiErrorMessage(err, 'Could not update presets.')),
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
      onClick={backdropClick(onClose)}
    >
      <div
        className="flex max-h-[90vh] w-full min-w-[320px] max-w-6xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <Layers className="h-4 w-4 shrink-0" />
            Presets
          </h3>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => state.setShowCreateRuleBased(true)}
              disabled={state.isBusy}
            >
              <Filter className="h-3.5 w-3.5" />
              Create rule-based preset
            </Button>
            {selectedDrafts.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={() => state.setShowCreate(true)}
                disabled={state.isBusy}
              >
                <Plus className="h-3.5 w-3.5" />
                Save {selectedDrafts.length} as preset
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <PresetFolderChips
          catalogFolders={presetFoldersQuery.data ?? []}
          presets={state.presets}
          activeFolderId={folderBulk.folderFilterId}
          onSelect={folderBulk.setFolderFilterId}
          onManageFolders={() => setManageFoldersOpen(true)}
        />
        <PresetFolderBulkBar
          folders={presetFoldersQuery.data ?? []}
          visiblePresetIds={folderBulk.filtered.map((p) => p.id)}
          selectedPresetIds={selectedPresetIdList}
          disabled={state.isBusy}
          isBusy={bulkPresetFolders.isPending}
          onAddToFolder={(id) => runBulkFolder('add', id)}
          onSetFolderOnly={(id) => runBulkFolder('replace', id)}
          onSelectAllVisible={folderBulk.selectAllVisible}
          onDeselectAll={folderBulk.deselectAll}
        />
        <PresetFoldersManageDialog
          open={manageFoldersOpen}
          onOpenChange={setManageFoldersOpen}
          folders={presetFoldersQuery.data ?? []}
          isLoading={presetFoldersQuery.isLoading}
          isBusy={folderMutationBusy}
          onRename={handleFolderRename}
          onDelete={handleFolderDelete}
          onCreate={handleCreateFolder}
          isCreateBusy={createPresetFolder.isPending}
        />
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <SortablePresetList
            presets={folderBulk.filtered}
            tagColors={state.tagColors}
            selectedDrafts={selectedDrafts}
            isLoading={state.presetsQuery.isLoading}
            onApply={state.handleApply}
            onEdit={state.handleEdit}
            onDelete={state.handleDelete}
            onAddToPreset={state.handleAddToPreset}
            isApplying={state.applyPreset.isPending}
            isBusy={state.isBusy}
            selectionMode={state.presets.length > 0}
            selectedIds={folderBulk.bulkSelected}
            onToggleSelected={folderBulk.toggleBulk}
            bulkSelected={folderBulk.bulkSelected}
            onToggleGroupBulk={folderBulk.toggleBulkGroup}
            onReorder={(ids) =>
              state.handlePresetsReorder(ids, folderBulk.filtered, folderBulk.folderFilterId)
            }
            onApplyGroup={(ids, match) => applyGroup(ids, match)}
            bulkMatchListOrder={folderBulk.matchListOrder}
            bulkCampaignOk={bulkCampaignId >= 1}
            bulkRoutingFilled={bulkRoutingFilled}
          />
        </div>
        {state.presets.length > 0 && (
          <PresetsModalBulkBar
            bulkWorkDate={bulkWorkDate}
            onBulkWorkDateChange={setBulkWorkDate}
            bulkCampaignId={bulkCampaignId}
            onBulkCampaignChange={setBulkCampaignId}
            campaigns={catalog.campaigns}
            matchListOrder={folderBulk.matchListOrder}
            onMatchListOrderChange={folderBulk.setMatchListOrder}
            selectedCount={folderBulk.bulkSelected.size}
            selectedDropCount={bulkSelectedDropCount}
            disabled={state.applyPreset.isPending}
            onApplySelected={applySelectedBulk}
            showRoutingRow={anyCatalogPresetNeedsRouting}
            gateways={catalog.gateways}
            tlds={catalog.tlds}
            bulkRouteId={bulkRouteId}
            onBulkRouteIdChange={setBulkRouteId}
            bulkShortLinkTld={bulkShortLinkTld}
            onBulkShortLinkTldChange={setBulkShortLinkTld}
            selectionNeedsRouting={bulkSelectionNeedsRouting}
          />
        )}
      </div>

      {state.applyingPreset && (
        <div
          className="fixed inset-0 z-[210] flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
          onClick={backdropClick(handleApplyOverlayClick)}
        >
          <div
            className="flex w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-border bg-muted/20 px-4 py-3 text-sm font-semibold">
              Apply preset
            </div>
            <ApplyPresetModal
              preset={state.applyingPreset}
              workDate={state.applyWorkDate}
              campaignWiserId={state.applyCampaignWiserId}
              campaigns={catalog.campaigns}
              onCampaignChange={state.setApplyCampaignWiserId}
              isBusy={state.applyPreset.isPending}
              countryKey={countryKey}
              catalog={catalog}
              smsTexts={state.creative?.smsTexts ?? []}
              senderNames={state.creative?.senderNames ?? []}
              tagColors={state.tagColors}
              dropCount={dropCount}
              dropLabels={dropLabels}
              onClose={() => state.setApplyingPreset(null)}
              onAiGeneratingChange={setIsApplyAiGenerating}
              onConfirm={state.handleApplyConfirm}
              onWorkDateChange={state.setApplyWorkDate}
            />
          </div>
        </div>
      )}

      {state.showCreate && selectedDrafts.length > 0 && (
        <CreatePresetModal
          countryKey={countryKey}
          drops={selectedDrafts}
          availableGroupNames={state.availableGroupNames}
          isBusy={state.createPreset.isPending}
          onClose={() => state.setShowCreate(false)}
          onCreate={state.handleCreate}
        />
      )}

      {state.showCreateRuleBased && (
        <CreateRuleBasedPresetModal
          catalog={catalog}
          countryKey={countryKey}
          availableTags={state.availableTags}
          availableGroupNames={state.availableGroupNames}
          tagColors={state.tagColors}
          isBusy={state.createPreset.isPending}
          onClose={() => state.setShowCreateRuleBased(false)}
          onCreate={state.handleCreate}
        />
      )}

      {state.editingPreset?.presetType === 'rule-based' ? (
        <EditRuleBasedPresetModal
          countryKey={countryKey}
          preset={state.editingPreset}
          catalog={catalog}
          availableTags={state.availableTags}
          availableGroupNames={state.availableGroupNames}
          tagColors={state.tagColors}
          isBusy={state.updatePreset.isPending}
          onClose={() => state.setEditingPreset(null)}
          onSave={state.handleEditSave}
        />
      ) : (
        state.editingPreset && (
          <EditPresetModal
            key={state.editingPreset.id}
            countryKey={countryKey}
            preset={state.editingPreset}
            catalog={catalog}
            availableGroupNames={state.availableGroupNames}
            isBusy={state.updatePreset.isPending}
            onClose={() => state.setEditingPreset(null)}
            onSave={state.handleEditSave}
          />
        )
      )}
    </div>
  );
};
